
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../../lib/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import Header from '../../../../components/feature/Header';
import Footer from '../../../../components/feature/Footer';

interface BookingDetails {
  id: string;
  listing_id: string;
  renter_id: string;
  host_id: string;
  start_date: string;
  end_date: string;
  total_amount: number;
  status: string;
  listings: {
    id: string;
    title: string;
    images: string[];
  } | null;
}

export default function ReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [booking, setBooking] = useState<BookingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasExistingReview, setHasExistingReview] = useState(false);
  
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');

  useEffect(() => {
    const fetchBookingAndCheckReview = async () => {
      if (!id || !user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch booking details
        const { data: bookingData, error: bookingError } = await supabase
          .from('bookings')
          .select(`
            id,
            listing_id,
            renter_id,
            host_id,
            start_date,
            end_date,
            total_amount,
            status,
            listings:listing_id (
              id,
              title,
              images
            )
          `)
          .eq('id', id)
          .maybeSingle();

        if (bookingError) throw bookingError;

        if (!bookingData) {
          setError('Booking not found');
          setLoading(false);
          return;
        }

        // Verify user is the renter
        if (bookingData.renter_id !== user.id) {
          setError('You can only review your own bookings');
          setLoading(false);
          return;
        }

        // Verify booking is completed
        if (bookingData.status !== 'completed' && bookingData.status !== 'active' && bookingData.status !== 'confirmed') {
          setError('You can only review confirmed or completed bookings');
          setLoading(false);
          return;
        }

        setBooking(bookingData);

        // Check for existing review
        const { data: existingReview, error: reviewError } = await supabase
          .from('reviews')
          .select('id')
          .eq('booking_id', id)
          .eq('reviewer_id', user.id)
          .maybeSingle();

        if (reviewError && reviewError.code !== 'PGRST116') throw reviewError;

        if (existingReview) {
          setHasExistingReview(true);
        }
      } catch (_err) {
        setError('Failed to load booking details');
      } finally {
        setLoading(false);
      }
    };

    fetchBookingAndCheckReview();
  }, [id, user?.id]);

  const handleSubmit = async () => {
    if (!booking || !user?.id || rating === 0) return;

    setSubmitting(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('reviews')
        .insert({
          listing_id: booking.listing_id,
          reviewer_id: user.id,
          booking_id: booking.id,
          reviewee_id: booking.host_id,
          rating: rating,
          review_text: comment.trim() || null,
          review_type: 'renter_to_host'
        });

      if (insertError) throw insertError;

      // Redirect to listing page after successful submission
      navigate(`/listing/${booking.listing_id}`, { 
        state: { reviewSubmitted: true } 
      });
    } catch (err: any) {
      if (err.code === '23505') {
        setError('You have already submitted a review for this booking');
      } else {
        setError('Failed to submit review. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96 pt-32">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-[#14B8A6] animate-spin"></i>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error && !booking) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96 pt-32">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-red-500"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/bookings')}
              className="px-6 py-3 bg-[#14B8A6] text-white rounded-lg hover:bg-[#0F9C8E] transition-colors cursor-pointer whitespace-nowrap"
            >
              Back to Bookings
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Already reviewed state
  if (hasExistingReview) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96 pt-32">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-500"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Review Already Submitted</h3>
            <p className="text-gray-600 mb-6">You have already reviewed this booking</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/bookings')}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                Back to Bookings
              </button>
              <button
                onClick={() => navigate(`/listing/${booking?.listing_id}`)}
                className="px-6 py-3 bg-[#14B8A6] text-white rounded-lg hover:bg-[#0F9C8E] transition-colors cursor-pointer whitespace-nowrap"
              >
                View Listing
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1 pt-32 pb-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/bookings')}
            className="flex items-center gap-2 text-gray-600 hover:text-[#14B8A6] mb-6 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="font-medium">Back to Bookings</span>
          </button>

          {/* Page Title */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Leave a Review</h1>
            <p className="text-gray-600 mt-2">Share your experience with this storage space</p>
          </div>

          {/* Listing Info Card */}
          {booking?.listings && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-8">
              <div className="flex gap-4">
                <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={booking.listings.images?.[0] || 'https://readdy.ai/api/search-image?query=empty%20storage%20space%20clean%20minimal%20interior&width=200&height=200&seq=review-placeholder&orientation=squarish'}
                    alt={booking.listings.title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-lg truncate">{booking.listings.title}</h3>
                  <p className="text-gray-500 text-sm mt-2 flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    <span>{formatDate(booking.start_date)} - {formatDate(booking.end_date)}</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Review Form */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Star Rating */}
            <div className="mb-8">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                How was your experience?
              </label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="w-12 h-12 flex items-center justify-center cursor-pointer transition-transform hover:scale-110"
                  >
                    <i
                      className={`text-4xl ${
                        star <= (hoverRating || rating)
                          ? 'ri-star-fill text-yellow-400'
                          : 'ri-star-line text-gray-300'
                      }`}
                    ></i>
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="mt-2 text-sm text-gray-600">
                  {rating === 1 && 'Poor'}
                  {rating === 2 && 'Fair'}
                  {rating === 3 && 'Good'}
                  {rating === 4 && 'Very Good'}
                  {rating === 5 && 'Excellent'}
                </p>
              )}
            </div>

            {/* Comment */}
            <div className="mb-8">
              <label className="block text-lg font-semibold text-gray-900 mb-3">
                Write a review <span className="text-gray-400 font-normal text-sm">(optional)</span>
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 1000))}
                placeholder="Tell others about your experience with this storage space..."
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent resize-none text-sm"
              />
              <p className="mt-2 text-sm text-gray-500 text-right">
                {comment.length}/1000 characters
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <i className="ri-error-warning-line text-red-500"></i>
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={rating === 0 || submitting}
              className={`w-full py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer whitespace-nowrap ${
                rating === 0 || submitting
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-[#14B8A6] text-white hover:bg-[#0F9C8E]'
              }`}
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-send-plane-line"></i>
                  Submit Review
                </span>
              )}
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
